
import Vue, { PropType } from 'vue'
import { deserialise, serialise } from 'kitsu-core'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import AppMixin from '@/_mixins/app'
import ProjectModel from '@/views/Project/Project/models/project'
import { getRequest, postRequest } from '@/plugins/api-axios'
import { getURL } from '@/utils/axios'
import ProjectAutocomplete from '@/_components/autocomplete/project/project.vue'

export default Vue.extend({
    name: 'CopyFormDialog',
    mixins: [AppMixin],
    props: {
        config: {
            type: Object as PropType<{
                formBaseUrl: string
                resourcePath: string
                resourceBaseUrl: string
                resourceType: string
                getParams?: Record<string, string | number | boolean>
            }>,
            required: true,
        },
        showCopyFormHelpText: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        DialogTemplate,
        ProjectAutocomplete,
    },
    data: () => ({
        project: null as Maybe<ProjectModel>,
        formList: [] as Maybe<any>[],
        selectedForm: null as Maybe<any>,
        isFetchingForms: false,
        isFetchingRevisions: false,
        isCopyingForm: false,
        formResource: null as Maybe<any>,
    }),
    computed: {
        activeFormRevisionTitle() {
            const activeRevision = this.formResource?.activeRevision?.data

            if (!activeRevision) return null

            if (activeRevision.data.title[this.userLanguage])
                return activeRevision.data.title[this.userLanguage]

            return activeRevision.data.title[activeRevision.data.languages[0]]
        },
    },
    methods: {
        async copyForm() {
            if (!this.activeFormRevisionTitle || !this.selectedForm?.id) return

            try {
                this.isCopyingForm = true
                const { data } = await postRequest(
                    getURL({
                        path: this.config.resourcePath,
                        base: this.config.resourceBaseUrl,
                        params: { copyOf: this.selectedForm.id },
                    }),
                    {},
                    serialise(this.config.resourceType, {
                        project: {
                            data: {
                                type: 'project',
                                id: this.currentProjectId,
                            },
                        },
                    }).data
                )

                const newForm = deserialise(data).data
                // poll for at most 5 times to check if the form revision has been copied
                let counter = 0
                do {
                    const formData = await this.getForm(newForm.id)
                    if (
                        Array.isArray(formData.draftRevisions.data) &&
                        formData.draftRevisions.data.length > 0
                    ) {
                        this.$emit('formCopied', newForm)
                        break
                    }
                    await new Promise((resolve) => setTimeout(resolve, 1000))
                } while (counter++ < 5)
            } finally {
                this.handleDialogClosed()
            }
        },

        handleDialogClosed() {
            this.$emit('input', false)
        },

        async handleProjectSelected(project: ProjectModel) {
            this.project = project
            this.selectedForm = null
            this.formResource = null
            this.formList = []

            if (!this.project?._id) return

            this.isFetchingForms = true
            let { data } = await getRequest(
                getURL({
                    path: this.config.resourcePath,
                    base: this.config.resourceBaseUrl,
                    params: {
                        'filter[project]': this.project._id,
                        'filter[isArchived]': false,
                        'page[limit]': 100,
                        'filter[hasActiveRevision]': true,
                        sort: '-updatedAt',
                        ...(this.config.getParams && this.config.getParams),
                    },
                })
            )
            this.formList = deserialise(data).data
            if (this.formList.length === 1) this.handleFormSelected(this.formList[0].id)
            this.isFetchingForms = false
        },

        async getForm(id: string) {
            const res = await getRequest(
                getURL({
                    path: `forms`,
                    base: this.config.formBaseUrl,
                    params: {
                        'filter[resource]': [id],
                        'page[limit]': 1,
                        include: 'draftRevisions',
                    },
                })
            )

            return deserialise(res.data).data[0]
        },

        async handleFormSelected(form: any) {
            this.selectedForm = form

            if (!this.selectedForm) return

            this.isFetchingRevisions = true
            const { data } = await getRequest(
                getURL({
                    path: `forms`,
                    base: this.config.formBaseUrl,
                    params: {
                        'filter[resource]': [this.selectedForm.id],
                        include: 'activeRevision',
                    },
                })
            )
            this.formResource = deserialise(data).data[0]
            this.isFetchingRevisions = false
        },
    },
})
